import { createRoute, lazyRouteComponent } from '@tanstack/react-router'

import PageSkeleton from '@/core/components/page-skeleton'
import RouteError from '@/core/components/route-error'
import { queryClient } from '@/core/lib/query-client'
import { routeHasPermission } from '@/core/utils/auth-utils'
import { listSearchSchema } from '@/core/lib/table-search-schema'

import { dashboardRoute } from '@/modules/dashboard/dashboard.routes'
import { roleListQueryOptions } from '@/modules/role/role.hooks'
import {
  userListQueryOptions,
  userPagedQueryOptions,
} from '@/modules/user/user.hooks'

export const usersRoute = createRoute({
  path: '/users',
  getParentRoute: () => dashboardRoute,
  errorComponent: ({ error, reset }) => (
    <RouteError error={error} reset={reset} />
  ),
})

export const usersListRoute = createRoute({
  getParentRoute: () => usersRoute,
  path: '/',
  validateSearch: listSearchSchema,
  beforeLoad: ({ preload }) =>
    routeHasPermission(
      preload,
      'user.list',
      'Kullanıcı listesine erişim izniniz yok.'
    ),
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    query: search.query ?? '',
  }),
  loader: ({ deps }) =>
    queryClient.ensureQueryData(userPagedQueryOptions(deps.page, deps.query)),
  head: () => ({ meta: [{ title: 'Kullanıcılar' }] }),
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/user/pages/list-user.page')
  ),
})

export const usersCreateRoute = createRoute({
  getParentRoute: () => usersRoute,
  path: 'create',
  beforeLoad: ({ preload }) =>
    routeHasPermission(
      preload,
      'user.create',
      'Kullanıcı oluşturmak için yetkiniz yok.'
    ),
  loader: () => queryClient.ensureQueryData(roleListQueryOptions()),
  head: () => ({ meta: [{ title: 'Kullanıcı Oluştur' }] }),
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/user/pages/create-user.page')
  ),
})

export const usersDetailRoute = createRoute({
  getParentRoute: () => usersRoute,
  path: '$id',
  beforeLoad: ({ preload }) =>
    routeHasPermission(
      preload,
      'user.list',
      'Kullanıcı detayına erişim izniniz yok.'
    ),
  loader: () =>
    Promise.all([
      queryClient.ensureQueryData(userListQueryOptions()),
      queryClient.ensureQueryData(roleListQueryOptions()),
    ]),
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/user/pages/detail-user.page')
  ),
})

export const usersEditRoute = createRoute({
  getParentRoute: () => usersRoute,
  path: '$id/edit',
  beforeLoad: ({ preload }) =>
    routeHasPermission(
      preload,
      'user.update',
      'Kullanıcı düzenlemek için yetkiniz yok.'
    ),
  loader: () =>
    Promise.all([
      queryClient.ensureQueryData(userListQueryOptions()),
      queryClient.ensureQueryData(roleListQueryOptions()),
    ]),
  head: () => ({ meta: [{ title: 'Kullanıcı Düzenle' }] }),
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/user/pages/edit-user.page')
  ),
})
